import React, { useState, useEffect } from 'react';
import { analyticsAPI, cardsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Users, Gift, TrendingUp, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [cardAnalytics, setCardAnalytics] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCard && selectedCard !== 'all') {
      fetchCardAnalytics(selectedCard);
    } else {
      setCardAnalytics(null);
    }
  }, [selectedCard]);

  const fetchData = async () => {
    try {
      const [analyticsRes, cardsRes] = await Promise.all([
        analyticsAPI.dashboard(),
        cardsAPI.list(),
      ]);
      setAnalytics(analyticsRes.data);
      setCards(cardsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardAnalytics = async (cardId) => {
    try {
      const { data } = await analyticsAPI.card(cardId);
      setCardAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch card analytics:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const displayStats = selectedCard === 'all' ? analytics : cardAnalytics;

  const chartData = analytics?.daily_stats?.map(stat => ({
    date: stat._id,
    transactions: stat.transactions,
    points: stat.points,
  })) || [];

  const stats = [
    { label: 'Total clients', value: displayStats?.total_customers || 0, icon: Users, color: 'blue' },
    { label: 'Points distribués', value: displayStats?.total_points_issued || 0, icon: Gift, color: 'green' },
    { label: 'Points utilisés', value: displayStats?.total_points_redeemed || 0, icon: Activity, color: 'purple' },
    { label: 'Total transactions', value: displayStats?.total_transactions || 0, icon: TrendingUp, color: 'orange' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Statistiques
          </h1>
          <p className="text-slate-600 mt-1">Suivez les performances de votre programme de fidélité</p>
        </div>
        <Select value={selectedCard} onValueChange={setSelectedCard}>
          <SelectTrigger className="w-48" data-testid="analytics-card-select">
            <SelectValue placeholder="Toutes les cartes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les cartes</SelectItem>
            {cards.map((card) => (
              <SelectItem key={card.id} value={card.id}>{card.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="border-slate-200 card-hover" data-testid={`analytics-stat-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value.toLocaleString()}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  stat.color === 'blue' ? 'bg-blue-50' :
                  stat.color === 'green' ? 'bg-green-50' :
                  stat.color === 'purple' ? 'bg-purple-50' : 'bg-orange-50'
                }`}>
                  <stat.icon className={`w-6 h-6 ${
                    stat.color === 'blue' ? 'text-blue-600' :
                    stat.color === 'green' ? 'text-green-600' :
                    stat.color === 'purple' ? 'text-purple-600' : 'text-orange-600'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Transactions dans le temps</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                    <Area type="monotone" dataKey="transactions" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.2} name="Transactions" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-slate-500">
                Pas encore de données
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Activité des points</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                    <Bar dataKey="points" fill="#10b981" name="Points" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-slate-500">
                Pas encore de données
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Redemption Rate */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Métriques clés</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="p-6 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-2">Taux d'utilisation</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics?.total_points_issued > 0 
                  ? ((analytics.total_points_redeemed / analytics.total_points_issued) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="text-xs text-slate-500 mt-1">Points utilisés vs distribués</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-2">Moy. points par client</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics?.total_customers > 0 
                  ? Math.round(analytics.total_points_issued / analytics.total_customers)
                  : 0}
              </p>
              <p className="text-xs text-slate-500 mt-1">Points moyens à vie</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-2">Moy. transactions par client</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics?.total_customers > 0 
                  ? (analytics.total_transactions / analytics.total_customers).toFixed(1)
                  : 0}
              </p>
              <p className="text-xs text-slate-500 mt-1">Indicateur d'engagement</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
